import React from "react";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { dataFieldFocus, fontFamily, field } from "../../../../theme/tokens";
import { ASSETS } from "../../../../utils/config";

const SearchField = ({
  height = 46,
  value = "",
  sx = {},
  placeholder = "Search",
  handleSearch = () => {},
  handleChange = () => {},
  isHideIcon = false,
  onChange = () => {},
  fullWidth = true,
  ariaLabel = "Search wheels",
  // QA-1261 — optional hard cap on typed/pasted length. Undefined by default so
  // every existing search caller is unaffected; the Create/Edit-Collection Title
  // field passes it (a collection name was otherwise unbounded, maxlength=null).
  maxLength = undefined,
}) => {
  return (
    <TextField
      autoComplete
      fullWidth={fullWidth}
      value={value}
      type="search"
      onChange={(e) => {
        handleChange(e.target.value);
        onChange(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          // stgqa-0927-P0-kelly-gate-b-gallery-collections-r100519-retry —
          // MEASURED live: without this, Enter here bubbled past a modal
          // portal (e.g. the Move-to-Collection dialog) up to `document`,
          // where the Gallery grid's roving-tabindex "Enter opens" handler
          // picked it up and navigated to whatever wheel was last selected,
          // silently closing the dialog mid-task.
          e.stopPropagation();
          handleSearch(value);
        }
      }}
      // onBlur={() => handleSearch(value)}
      placeholder={placeholder}
      inputProps={{
        "aria-label": ariaLabel,
        ...(maxLength != null ? { maxLength } : {}),
      }}
      sx={{
        height: `${height}px`,
        ...sx,
        // ISP-9999 (gordon-0916-themes-preview-search-bar-square-corners) —
        // this bgcolor used to sit on the TextField's own root sx (the
        // FormControl), which paints a plain 0-radius rectangle. The actual
        // rounded border lives one level down on .MuiOutlinedInput-root
        // (MUI's theme border-radius via the notchedOutline fieldset), so
        // that FormControl fill showed as white SQUARE corners poking out
        // past the rounded input underneath it — matching-corner-rounding
        // law: an overlapping fill must share the radius of the element it
        // sits behind. Painting the fill on .MuiOutlinedInput-root itself
        // means it always shares that element's own radius, corners
        // included.
        "& .MuiOutlinedInput-root": {
          height: `${height}px`,
          fontSize: "14px",
          bgcolor: "#ffffff",
          // gordon-0920-gallery-theme-corner-setting-not-applied-to-search-
          // bar-linkvault-box-create-wheel-button: this field kept MUI's
          // theme.shape.borderRadius default (4px, see theme/tokens.js's
          // focusRingRadius comment: "`.MuiOutlinedInput-root` container is
          // rounded to 4px") no matter what the Gallery Theme studio's "Card
          // corners" slider was set to (owner screenshot, 2026-09-20) — a
          // square-corner theme still rendered a rounded field. Same
          // --gt-card-radius token every other themed gallery surface reads;
          // this component is shared app-wide (not Gallery-only), and
          // --gt-card-radius is only ever set inside the Gallery page's own
          // themed subtree (pages/Gallery/index.jsx), so every other caller
          // keeps today's exact 4px, byte-identical, via CSS custom-property
          // inheritance. The focus effect (border-color, see
          // dataFieldFocus.muiSx below) paints on the notchedOutline fieldset
          // one level down, which shares this element's border-radius by
          // construction, so it tracks this automatically -- no separate
          // wiring.
          borderRadius: "var(--gt-card-radius, 4px)",
        },
        "& .MuiInputBase-input": {
          marginLeft: !isHideIcon ? "-12px" : "0px",
        },
        // ISP-9999 (gordon-0923-P1-REPEAT-kelly-field-focus-style-one-shared-
        // border-only-app-wide) — this used to paint a second purple ring
        // (outline on the inner input, moved to a box-shadow double-band on
        // the wrapper) INSTEAD of ever letting the field's own border turn
        // purple, which is what produced the "second outer ring / double
        // border" Kelly kept reporting. The app-wide shared rule now
        // (src/theme/tokens.js `dataFieldFocus`, mirrored in
        // src/styles/dataFieldFocus.css) is the ONE effect every data field
        // uses: the notchedOutline border recolors on focus, nothing else.
        // Resting/hover stay pinned to the gallery accent (or the flat grey
        // default outside a themed gallery) exactly as before — only the
        // FOCUSED color changed, from "stay grey" to "recolor to accent".
        // gordon-0921-WEB-gallery-theme-hover-and-selected-highlights-must-
        // use-the-theme-accent-colour-sweep: dataFieldFocus.muiSx's
        // notchedOutline border already resolves `--iy-field-focus-color`,
        // which itself falls through to the gallery theme's `--gt-accent`
        // inside a themed gallery subtree (src/styles/dataFieldFocus.css) —
        // this sweep's goal for this field is already covered by that one
        // shared token, nothing further to theme here.
        ...dataFieldFocus.muiSx,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--gt-search-border, rgba(0, 0, 0, 0.23))",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--gt-search-border, rgba(0, 0, 0, 0.23))",
        },
        // gordon-0920-gallery-and-header-control-typography — with no rule of
        // its own here, this placeholder fell through to the BROWSER's default
        // un-styled placeholder treatment: `color: inherit` at `opacity: 0.42`
        // (Chromium/WebKit), which MEASURED live as rgb(15,15,15) @ 0.42 ->
        // ~2.8:1 on white, failing WCAG AA's 4.5:1 text-contrast floor and
        // reading as the "grey, lighter" placeholder the owner's screenshot
        // flagged next to the header search's dark one. Pinned explicitly
        // (opacity:1 + an AA-passing ink) so it can never again depend on an
        // un-styled browser default, matching the header search's own
        // explicit `::placeholder` rule (SearchBar/styles.js). Reads
        // `field.muted` rather than `field.placeholder` (#98A2B3) — the
        // latter measures ~2.6:1 on white and would still fail AA here.
        "& .MuiOutlinedInput-input::placeholder": {
          fontFamily,
          color: field.muted,
          opacity: 1,
        },
      }}
      InputProps={{
        startAdornment: !isHideIcon && (
          <InputAdornment position="start">
            {/* Decorative search glyph — the input itself carries the label.
                gordon-0920-gallery-and-header-control-typography: was MUI's
                generic `@mui/icons-material/Search` glyph (dark, 18px,
                data-testid="SearchIcon") — a SECOND, different magnifier next
                to the header search's own line-art brand asset
                (ASSETS.SearchIcon2, purple, 20px, SearchBar/index.jsx). One
                search affordance, one icon: reuse the same shared asset so
                the app never shows two magnifiers again. */}
            <IconButton
              tabIndex={-1}
              disableRipple
              aria-hidden="true"
              sx={{ marginLeft: "-10px", pointerEvents: "none" }}
            >
              <img
                src={ASSETS.SearchIcon2}
                alt=""
                width={18}
                height={18}
                data-testid="search-field-icon"
              />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default SearchField;
